<template>
  <div class="timeline-sidebar">
    <div class="panel-section">
      <div class="section-title">素材搜索</div>
      <div class="search-tabs mb-3">
        <button :class="{ active: searchMode === 'video' }" @click="searchMode = 'video'">视频搜索</button>
        <button :class="{ active: searchMode === 'commodity_group' }" @click="searchMode = 'commodity_group'">商品讲解（分组）</button>
      </div>
      <div class="section-content">
        <!-- 搜索表单 -->
        <div class="search-form">
          <div class="d-flex align-center gap-2 mb-2">
            <v-autocomplete
              v-model="selectedKols"
              :items="kolList"
              item-title="name"
              item-value="id"
              label="筛选达人"
              multiple
              chips
              closable-chips
              density="compact"
              variant="outlined"
              hide-details
              class="flex-grow-1 custom-input"
              clearable
              @update:menu="val => { kolMenuOpen = val; if (val) kolChanged = false; if (!val && kolChanged) performSearch() }"
            ></v-autocomplete>
            <v-checkbox
              v-model="showAdvanced"
              label="按日期筛选"
              hide-details
              density="compact"
              color="primary"
              class="flex-shrink-0 date-checkbox"
            ></v-checkbox>
          </div>

          <!-- 日期筛选 (可选展开) -->
          <v-expand-transition>
            <div v-if="showAdvanced" class="d-flex mb-2 gap-2 align-center px-1">
              <input type="date" v-model="startDate" class="date-input" placeholder="开始日期">
              <span class="date-sep">-</span>
              <input type="date" v-model="endDate" class="date-input" placeholder="结束日期">
            </div>
          </v-expand-transition>
          
          <div class="d-flex gap-2 mb-1">
            <v-text-field
              v-model="keyword"
              :label="searchMode === 'video' ? '搜索时间片' : '搜索商品名称'"
              density="compact"
              variant="outlined"
              hide-details
              class="flex-grow-1 custom-input"
              clearable
              @keyup.enter="performSearch"
            >
              <template v-slot:append-inner>
                <v-icon @click="performSearch" class="cursor-pointer">mdi-magnify</v-icon>
              </template>
            </v-text-field>
            
            <v-btn 
              color="primary" 
              class="search-btn"
              :loading="isSearching" 
              @click="performSearch(true)"
            >
              搜 索
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- 搜索结果列表 -->
    <div class="results-container mt-3">
      <div v-if="isSearching" class="text-center py-4">
        <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
      </div>
      <div v-else-if="results.length === 0" class="text-center py-4 empty-text">
        暂无搜索结果
      </div>
      <div v-else-if="searchMode === 'commodity_group'" class="results-list commodity-results">
        <CommodityGroupItem
          v-for="(group, index) in results"
          :key="`${group.dou_number || ''}_${group.liveStartTime || ''}_${group.productName || index}`"
          :group="group"
          :expanded="expandedGroups.includes(index)"
          :selected-segment="selectedCommoditySegment"
          :active-segment="selectedCommoditySegment"
          dark
          @toggle="toggleGroup(index)"
          @select="handleCommoditySegment"
        />
      </div>
      <div v-else class="results-list">
        <div 
          v-for="(item, index) in results" 
          :key="index"
          class="result-card"
          :class="{ 'active-card': transcriptionId === item.transcription_id }"
          @click="handleAction(item, 'fetch')"
        >
          <div class="card-header">
            <span class="kol-tag" :title="item['达人昵称'] || item.anchor_name">
              <v-icon size="x-small" class="mr-1">mdi-account-circle</v-icon>
              {{ item['达人昵称'] || item.anchor_name || '未知达人' }}
            </span>
            
            <!-- 状态标签预留位 -->
            <v-chip 
              size="x-small" 
              :color="getStatusColor(item.clothing_analysis_status)" 
              variant="flat"
            >
              {{ getStatusText(item.clothing_analysis_status) }}
            </v-chip>
          </div>
          
          <div class="card-title text-truncate-2" :title="item.video_name || item.title">
            {{ item.video_name || item.title || '未知视频素材' }}
          </div>
          
          <div class="card-meta d-flex justify-space-between align-center mt-2">
            <span class="info-text">
              <v-icon size="10" class="mr-1">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.video_start_time || item.start_time) }}
            </span>
            <span v-if="item.commodity_count" class="info-text text-warning">
              <v-icon size="10" class="mr-1">mdi-shopping-outline</v-icon>
              {{ item.commodity_count }} 商品
            </span>
          </div>
          
          <div class="card-actions mt-3">
            <v-btn
              v-if="item.clothing_analysis_status === 'completed'"
              color="success"
              size="x-small"
              variant="tonal"
              block
              :loading="loading && transcriptionId === item.transcription_id"
              @click.stop="handleAction(item, 'fetch')"
            >
              加载轨道
            </v-btn>
            <v-btn
              v-else-if="item.clothing_analysis_status === 'processing'"
              color="warning"
              size="x-small"
              variant="tonal"
              block
              disabled
            >
              分析处理中...
            </v-btn>
            <v-btn
              v-else-if="canExtractClothingData"
              color="info"
              size="x-small"
              variant="outlined"
              block
              :loading="loading && transcriptionId === item.transcription_id"
              @click.stop="handleAction(item, 'generate')"
            >
               提取换装数据
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { get_accessable_kol_list, get_clip_material_list, query_outter_commodity_group } from '@/services/api.js'
import MessageUtil from '@/utils/MessageUtil'
import { useMaterialSearchPersistence } from '@/composables/useMaterialSearchPersistence'
import CommodityGroupItem from '@/components/common/CommodityGroupItem.vue'
import { normalizeCommodityGroups } from '@/utils/commodityGroups'

const props = defineProps({
  transcriptionId: String,
  loading: Boolean,
  status: String
})

const emit = defineEmits(['update:transcriptionId', 'generate', 'fetch'])

const canExtractClothingData = computed(() => {
  const userStr = localStorage.getItem('user')
  if (!userStr) return false
  try {
    const user = JSON.parse(userStr)
    
    // 1. 白名单手机号 (用户要求的硬编码控制)
    if (user && user.phoneNumber === '17372776468') {
      return true
    }
    
    // 2. 正常的权限控制逻辑 (从后端返回的 permissions 中判断)
    // FeaturePage.CONTENT_TIMELINE 的值是 "内容时间线"
    if (user && user.permissions) {
      return !!user.permissions["内容时间线"];
    }
    
    return false
  } catch (e) {
    return false
  }
})

// Search State
const searchMode = ref('video')
const {
  keyword: videoKeyword,
  selectedKols: videoSelectedKols,
  showAdvanced: videoShowAdvanced,
  startDate: videoStartDate,
  endDate: videoEndDate
} = useMaterialSearchPersistence()
const commodityKeyword = ref('')
const commoditySelectedKols = ref([])
const commodityShowAdvanced = ref(false)
const commodityStartDate = ref('')
const commodityEndDate = ref('')

const modeValue = (videoRef, commodityRef) => computed({
  get: () => searchMode.value === 'video' ? videoRef.value : commodityRef.value,
  set: value => {
    if (searchMode.value === 'video') videoRef.value = value
    else commodityRef.value = value
  }
})

const keyword = modeValue(videoKeyword, commodityKeyword)
const selectedKols = modeValue(videoSelectedKols, commoditySelectedKols)
const showAdvanced = modeValue(videoShowAdvanced, commodityShowAdvanced)
const startDate = modeValue(videoStartDate, commodityStartDate)
const endDate = modeValue(videoEndDate, commodityEndDate)
const kolList = ref([])
const searchResults = ref({ video: [], commodity_group: [] })
const searchingModes = ref({ video: false, commodity_group: false })
const results = computed(() => searchResults.value[searchMode.value])
const isSearching = computed(() => searchingModes.value[searchMode.value])
const kolMenuOpen = ref(false)
const kolChanged = ref(false)
const expandedGroups = ref([])
const selectedCommoditySegment = ref(null)

// 缓存最后一次搜索的有效参数，避免重复请求
const lastSearchStates = { video: '', commodity_group: '' }

// 自动触发搜索
watch(keyword, () => performSearch())
watch([startDate, endDate], () => {
  // 只有在开启了日期筛选的情况下，日期变化才触发搜索
  if (showAdvanced.value) performSearch()
})
watch(showAdvanced, (val) => {
  // 切换开关时，只有在已填写日期的情况下（即导致过滤逻辑实质变化）才触发
  if (startDate.value || endDate.value) {
    performSearch()
  }
})
watch(selectedKols, () => {
  if (kolMenuOpen.value) {
    kolChanged.value = true
  } else {
    performSearch()
  }
})
watch(searchMode, () => {
  kolChanged.value = false
  performSearch()
})

onMounted(async () => {
  // Fix for "ResizeObserver loop completed with undelivered notifications"
  const debounce = (fn) => {
    let frame
    return (...args) => {
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => fn(...args))
    }
  }

  const observer = window.ResizeObserver
  window.ResizeObserver = class ResizeObserver extends observer {
    constructor(callback) {
      super(debounce(callback))
    }
  }

  // Load KOL List
  try {
    const res = await get_accessable_kol_list('douyin')
    const data = res.data || res
    if (Array.isArray(data)) {
      kolList.value = data
    }
  } catch (e) {
    console.error('Failed to load KOLs', e)
  }
  
  // Optional: Auto fetch initial list
  performSearch()
})

let searchTimer = null
const performSearch = async (force = false) => {
  if (searchTimer) clearTimeout(searchTimer)
  
  searchTimer = setTimeout(async () => {
    const mode = searchMode.value
    // 构造当前有效搜索状态的快照
    const currentState = JSON.stringify({
      mode,
      kol: selectedKols.value,
      keyword: keyword.value,
      startDate: showAdvanced.value ? startDate.value : undefined,
      endDate: showAdvanced.value ? endDate.value : undefined
    })

    // 如果没有强制要求触发且搜索参数没变，则不重复请求
    // 强制触发通常用于“搜索”按钮的主动点击
    if (force !== true && currentState === lastSearchStates[mode]) return
    lastSearchStates[mode] = currentState

    searchingModes.value[mode] = true
    try {
      const kolListParam = (Array.isArray(selectedKols.value) && selectedKols.value.length)
        ? selectedKols.value.join(',')
        : undefined

      const requestParams = {
        key_word: keyword.value,
        kol_list: kolListParam,
        start_date: (showAdvanced.value && startDate.value) ? startDate.value : undefined,
        end_date: (showAdvanced.value && endDate.value) ? endDate.value : undefined
      }

      if (mode === 'commodity_group') {
        const res = await query_outter_commodity_group(requestParams)
        const data = res?.data?.data ?? res?.data ?? res
        searchResults.value.commodity_group = normalizeCommodityGroups(data)
        expandedGroups.value = []
      } else {
        const res = await get_clip_material_list({
          ...requestParams,
          exclude_virtual: true,
          with_detail: true
        })
        const data = res?.data?.data ?? res?.data ?? res
        searchResults.value.video = Array.isArray(data) ? data.map(item => ({
          ...item,
          clothing_analysis_status: item.clothing_analysis_status || 'none',
          commodity_count: item.commodity_count || 0
        })) : []
      }
    } catch (e) {
      MessageUtil.error(`${mode === 'video' ? '搜索素材' : '搜索商品'}失败: ${e.message || String(e)}`)
      searchResults.value[mode] = []
    } finally {
      searchingModes.value[mode] = false
    }
  }, 300)
}

const toggleGroup = (index) => {
  if (expandedGroups.value.includes(index)) {
    expandedGroups.value = expandedGroups.value.filter(item => item !== index)
  } else {
    expandedGroups.value.push(index)
  }
}

const handleCommoditySegment = (segment, group) => {
  if (!segment['已录制'] || !segment['录制ID']) {
    MessageUtil.warning('该讲解片段尚未录制，无法加载')
    return
  }

  selectedCommoditySegment.value = segment
  const item = {
    ...segment,
    transcription_id: segment['录制ID'],
    video_name: `${group.productName || segment['商品名称'] || '商品'} - 讲解片段`,
    hit_time: segment['讲解开始时间']
  }
  emit('update:transcriptionId', item.transcription_id)
  emit('fetch', item)
}

const handleAction = (item, action) => {
  if (!item.transcription_id) {
    MessageUtil.warning('该素材缺少有效 ID')
    return
  }
  emit('update:transcriptionId', item.transcription_id)
  emit(action, item)
}

// Helpers
const getStatusText = (status) => {
  if (status === 'completed') return '分析完成'
  if (status === 'processing') return '分析中'
  return '未分析'
}

const getStatusColor = (status) => {
  if (status === 'completed') return 'success'
  if (status === 'processing') return 'warning'
  return '#555555'
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return '未知时间'
  // Simplified formatting
  try {
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return `${d.getMonth()+1}-${d.getDate()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
  } catch {
    return dateStr
  }
}
</script>

<style scoped>
.timeline-sidebar {
  padding: 12px;
  background-color: #252525;
  height: 100%;
  overflow-y: auto;
  /* 针对 Firefox 的细滚动条 */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.timeline-sidebar::-webkit-scrollbar {
  width: 2px;
}
.timeline-sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1px;
}
.timeline-sidebar:hover::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.panel-section {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.section-title {
  color: #eee;
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #3d3d3d;
}

.search-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.search-tabs button {
  min-width: 0;
  padding: 7px 6px;
  border: 1px solid #444;
  border-radius: 4px;
  background: #1e1e1e;
  color: #999;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.search-tabs button:hover {
  color: #ddd;
  border-color: #666;
}

.search-tabs button.active {
  color: #00d2d3;
  border-color: #00aeb0;
  background: rgba(0, 210, 211, 0.08);
}

.search-form {
  display: flex;
  flex-direction: column;
}

.gap-2 {
  gap: 8px;
}

.search-btn {
  height: 40px !important;
  min-width: 64px !important;
  padding: 0 16px !important;
}

.date-checkbox :deep(.v-label) {
  font-size: 13px !important;
  color: #999 !important;
  padding-inline-start: 4px !important;
}

.date-input {
  flex: 1;
  background: #1e1e1e;
  border: 1px solid #444;
  color: #ccc;
  padding: 4px 8px;
  font-size: 14px;
  border-radius: 4px;
  outline: none;
}
.date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}
.date-input:focus {
  border-color: #00d2d3;
}
.date-sep {
  color: #777;
  padding: 4px 2px;
}

/* 覆盖 Vuetify 的深色背景样式 */
:deep(.custom-input .v-field) {
  background-color: #1e1e1e !important;
  border-radius: 4px;
}
:deep(.custom-input .v-field__outline) {
  color: #444 !important;
}
:deep(.custom-input .v-label) {
  color: #888 !important;
  font-size: 14px;
}
:deep(.custom-input input),
:deep(.custom-input .v-select__selection-text),
:deep(.custom-input .v-select__selection) {
  color: #ddd !important;
  font-size: 14px;
}

/* 改善清空按钮、下拉箭头和搜索图标的可见度 */
:deep(.v-field__clearable .v-icon),
:deep(.v-autocomplete__menu-icon),
:deep(.v-field__append-inner .v-icon),
:deep(.v-chip__close) {
  color: #ffffff !important;
  opacity: 0.8 !important;
}

:deep(.v-field__append-inner .v-icon:hover),
:deep(.v-field__clearable .v-icon:hover) {
  opacity: 1 !important;
}

:deep(.custom-input .v-chip) {
  background-color: #4a4a4a !important;
  color: #ffffff !important;
  border: 1px solid #555 !important;
}

:deep(.custom-input .v-chip:hover) {
  background-color: #5a5a5a !important;
}

.results-container {
  margin-top: 12px;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.commodity-results {
  gap: 0;
  padding-right: 0;
}

.empty-text {
  color: #777;
  font-size: 14px;
}

/* Card Styles */
.result-card {
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 10px;
  transition: all 0.2s;
}
.result-card:hover {
  border-color: #555;
  background: #2a2a2a;
}
.active-card {
  border-color: #00d2d3 !important;
  background: rgba(0, 210, 211, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.kol-tag {
  color: #4db8ff;
  font-size: 13px;
  display: flex;
  align-items: center;
  background: rgba(77, 184, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.card-title {
  color: #eee;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 6px;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.info-text {
  color: #888;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.card-actions {
  display: flex;
  gap: 8px;
}
</style>
